import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { Box, Button } from "@mui/material";
import { ReactNode } from "react";

import {
    Apartment,
    Apps,
    BarChart,
    Class,
    Dashboard,
    DateRange,
    EmojiTransportation,
    LocalShipping,
    Person,
    Settings,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { URLHandler } from "src/services/URLHandler";
import { FoodsIcon } from "@foodpilot/foods";
import {
    BRAND,
    CAMPAIGN,
    COMPANY,
    COMPANY_SECTOR,
    CREATE,
    DELETE,
    INGREDIENT_LINE,
    MODIFY,
    PRODUCT_LINE,
    SCORE_TYPE,
    STRATEGY,
    useFoodpilotContext,
    USER,
} from "src/context/FoodpilotContext.tsx";

export default function SettingsLayout() {
    const navigate = useNavigate();
    const location = useLocation();
    const links = useSettingsLinks();

    const getVariant = (route: string) => {
        if (route === URLHandler.settings.list()) {
            return location.pathname === route ? "selected-submenu" : "default-menu";
        }
        return location.pathname.includes(route) ? "selected-submenu" : "default-menu";
    };

    return (
        <Box sx={{ minHeight: "fit-content", display: "flex", flexDirection: "column", height: "100vh" }}>
            <Box
                sx={{
                    height: "fit-content",
                    padding: "1rem",
                    boxShadow: "0 5px 5px linear-gradient(180deg, #484945 0%, #2A2B27 100%)",
                    background: "#484945",
                    display: "flex",
                    alignItems: "center",
                    flexShrink: 0,
                    flexWrap: "wrap",
                    gap: "1rem",
                }}
            >
                {links
                    .filter((sl) => sl.visible)
                    .map((sl, index) => (
                        <Button
                            key={index}
                            variant={getVariant(sl.to)}
                            startIcon={sl.icon}
                            onClick={() => navigate(sl.to)}
                        >
                            {sl.title}
                        </Button>
                    ))}
            </Box>
            <Box
                sx={{
                    flexGrow: 1,
                    overflow: "auto",
                    "::-webkit-scrollbar": {
                        display: "none",
                    },
                }}
            >
                <Outlet />
            </Box>
        </Box>
    );
}

type SettingsLink = {
    title: string;
    to: string;
    icon: ReactNode;
    visible: boolean;
};

const useSettingsLinks = (): SettingsLink[] => {
    const { t } = useTranslation();
    const { canUser, isAdmin } = useFoodpilotContext();

    return [
        {
            title: "Home",
            to: URLHandler.settings.cms.page("home"),
            icon: <FoodsIcon size={2} icon="settings" />,
            visible: isAdmin(),
        },
        {
            title: "Strategy",
            to: URLHandler.settings.csrStrategy.general(),
            icon: <FoodsIcon size={2} icon="settings" />,
            visible: canUser(CREATE, STRATEGY) || canUser(MODIFY, STRATEGY) || canUser(DELETE, STRATEGY),
        },
        {
            title: t("Tableau de bord"),
            to: URLHandler.settings.list(),
            icon: <Dashboard />,
            visible: isAdmin(),
        },
        {
            title: t("settings.scores"),
            to: URLHandler.settings.scores(),
            icon: <BarChart />,
            visible: canUser(CREATE, SCORE_TYPE) || canUser(MODIFY, SCORE_TYPE) || canUser(DELETE, SCORE_TYPE),
        },
        {
            title: t("settings.campaign"),
            to: URLHandler.settings.campaigns(),
            icon: <DateRange />,
            visible: canUser(CREATE, CAMPAIGN) || canUser(MODIFY, CAMPAIGN) || canUser(DELETE, CAMPAIGN),
        },
        {
            title: t("settings.lines"),
            to: URLHandler.settings.productLine.list(),
            icon: <Class />,
            visible: canUser(CREATE, PRODUCT_LINE) || canUser(MODIFY, PRODUCT_LINE) || canUser(DELETE, PRODUCT_LINE),
        },
        {
            title: t("settings.company"),
            to: URLHandler.settings.companies.list(),
            icon: <Apartment />,
            visible: canUser(CREATE, COMPANY) || canUser(MODIFY, COMPANY) || canUser(DELETE, COMPANY),
        },
        {
            title: t("settings.companySectors"),
            to: URLHandler.settings.companySectors(),
            icon: <EmojiTransportation />,
            visible:
                canUser(CREATE, COMPANY_SECTOR) || canUser(MODIFY, COMPANY_SECTOR) || canUser(DELETE, COMPANY_SECTOR),
        },
        {
            title: t("settings.providers"),
            to: URLHandler.settings.providers.list(),
            icon: <LocalShipping />,
            visible: canUser(CREATE, COMPANY) || canUser(MODIFY, COMPANY) || canUser(DELETE, COMPANY),
        },
        {
            title: t("settings.users"),
            to: URLHandler.settings.users.list(),
            icon: <Person />,
            visible: canUser(CREATE, USER) || canUser(MODIFY, USER) || canUser(DELETE, USER),
        },
        {
            title: t("Roles"),
            to: URLHandler.settings.roles.list(),
            icon: <Person />,
            visible: isAdmin(),
        },
        {
            title: t("evea.askor.menu.button"),
            to: URLHandler.settings.evea.main(),
            icon: <Settings />,
            visible: isAdmin(),
        },
        {
            title: "CsrTheme",
            to: URLHandler.settings.csrTheme.list(),
            icon: <Person />,
            visible: isAdmin(),
        },
        {
            title: "Brand",
            to: URLHandler.settings.brands.list(),
            icon: <Settings />,
            visible: canUser(CREATE, BRAND) || canUser(MODIFY, BRAND) || canUser(DELETE, BRAND),
        },
        {
            title: "Filière",
            to: URLHandler.settings.ingredientLine.list(),
            icon: <Settings />,
            visible:
                canUser(CREATE, INGREDIENT_LINE) ||
                canUser(MODIFY, INGREDIENT_LINE) ||
                canUser(DELETE, INGREDIENT_LINE),
        },
        {
            title: "Applications",
            to: URLHandler.settings.applications.list(),
            icon: <Apps />,
            visible: isAdmin(),
        },
        {
            title: t("settings.infos"),
            to: URLHandler.settings.infos(),
            icon: <Settings />,
            visible: isAdmin(),
        },
        {
            title: t("settings.applicationParameter"),
            to: URLHandler.settings.appParams(),
            icon: <Settings />,
            visible: isAdmin(),
        },
    ];
};
